import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database'

// Load from environment variables (trim to remove any newlines)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL?.trim()
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim()

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.')
}

/**
 * Custom storage implementation for secure token storage
 * Uses sessionStorage with fallback to memory storage for better security
 */
const createSecureStorage = () => {
  // In-memory fallback for when storage is not available
  let memoryStorage: { [key: string]: string } = {}

  return {
    getItem: (key: string) => {
      try {
        // Try sessionStorage first (more secure than localStorage)
        const item = sessionStorage.getItem(key)
        if (item) return item

        // Fallback to memory storage
        return memoryStorage[key] || null
      } catch (error) {
        console.warn('[STORAGE] Failed to get item:', error)
        return memoryStorage[key] || null
      }
    },
    setItem: (key: string, value: string) => {
      try {
        // Store in sessionStorage (cleared when browser closes)
        sessionStorage.setItem(key, value)
        // Also keep in memory as backup
        memoryStorage[key] = value
      } catch (error) {
        console.warn('[STORAGE] Failed to set item, using memory only:', error)
        memoryStorage[key] = value
      }
    },
    removeItem: (key: string) => {
      try {
        sessionStorage.removeItem(key)
        delete memoryStorage[key]
      } catch (error) {
        console.warn('[STORAGE] Failed to remove item:', error)
        delete memoryStorage[key]
      }
    }
  }
}

// Create Supabase client with enhanced security settings
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // Session configuration
    autoRefreshToken: true,        // Auto-refresh tokens before expiry
    persistSession: true,           // Persist session across page refreshes
    detectSessionInUrl: true,       // Detect auth params in URL (for magic links)

    // Storage configuration - Use secure storage implementation
    storage: createSecureStorage(),
    storageKey: 'sb-auth-token',   // Custom storage key

    // Security settings
    flowType: 'pkce',              // Use PKCE flow for enhanced security

    // Debug settings (disable in production)
    debug: import.meta.env.DEV,
  },

  // Global settings
  global: {
    headers: {
      'X-Client-Info': 'mission-solar-knowledge-v3'
    }
  },

  // Database settings
  db: {
    schema: 'public'
  },

  // Realtime settings
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

/**
 * Auth helper functions
 */
export const authHelpers = {
  /**
   * Get current session with validation
   */
  getValidSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        console.error('[AUTH] Session error:', error)
        return null
      }

      // Validate session is not expired
      if (session && session.expires_at) {
        const expiresAt = new Date(session.expires_at * 1000)
        const now = new Date()

        if (expiresAt <= now) {
          console.warn('[AUTH] Session expired, needs refresh')
          // Attempt to refresh
          const { data: refreshData } = await supabase.auth.refreshSession()
          return refreshData.session
        }
      }

      return session
    } catch (error) {
      console.error('[AUTH] Failed to get valid session:', error)
      return null
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: async () => {
    const session = await authHelpers.getValidSession()
    return !!session
  },

  /**
   * Get current user with validation
   */
  getCurrentUser: async () => {
    const session = await authHelpers.getValidSession()
    if (!session) return null

    const { data: { user }, error } = await supabase.auth.getUser()

    if (error) {
      console.error('[AUTH] User fetch error:', error)
      return null
    }

    return user
  },

  /**
   * Force session refresh
   */
  forceRefresh: async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()

      if (error) {
        console.error('[AUTH] Force refresh error:', error)
        throw error
      }

      return data.session
    } catch (error) {
      console.error('[AUTH] Force refresh failed:', error)
      throw error
    }
  },

  /**
   * Sign out completely (clear all sessions)
   */
  signOutCompletely: async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut()

      // Clear all storage
      sessionStorage.clear()

      // Clear any localStorage items (cleanup from old system)
      localStorage.removeItem('userEmail')
      localStorage.removeItem('userId')
      localStorage.removeItem('sb-auth-token')

      console.log('[AUTH] Complete sign out successful')
    } catch (error) {
      console.error('[AUTH] Sign out error:', error)
      // Force clear even on error
      sessionStorage.clear()
      localStorage.clear()
    }
  }
}

// Helper function for real-time subscriptions
export const createRealtimeChannel = (channelName: string) => {
  return supabase.channel(channelName)
}

// Helper function for vector search
export const vectorSearch = async (
  queryEmbedding: number[],
  matchThreshold: number = 0.7,
  matchCount: number = 10,
  filterStatus?: string,
  filterCategory?: string
) => {
  // Ensure we have a valid session before making RLS-protected calls
  const session = await authHelpers.getValidSession()
  if (!session) {
    throw new Error('Authentication required for vector search')
  }

  const { data, error } = await supabase.rpc('match_faqs', {
    query_embedding: queryEmbedding,
    match_threshold: matchThreshold,
    match_count: matchCount,
    filter_status: filterStatus,
    filter_category: filterCategory
  })

  if (error) {
    console.error('Vector search error:', error)
    throw error
  }

  return data
}

// Helper function for hybrid search
export const hybridSearch = async (
  searchQuery: string,
  queryEmbedding: number[],
  matchThreshold: number = 0.7,
  matchCount: number = 10
) => {
  // Ensure we have a valid session before making RLS-protected calls
  const session = await authHelpers.getValidSession()
  if (!session) {
    throw new Error('Authentication required for hybrid search')
  }

  const { data, error } = await supabase.rpc('hybrid_search_faqs', {
    search_query: searchQuery,
    query_embedding: queryEmbedding,
    match_threshold: matchThreshold,
    match_count: matchCount
  })

  if (error) {
    console.error('Hybrid search error:', error)
    throw error
  }

  return data
}

// Helper function for bulk operations
export const bulkUpdateStatus = async (
  ids: string[],
  status: string,
  approvedBy?: string
) => {
  // Ensure we have a valid session before making RLS-protected calls
  const session = await authHelpers.getValidSession()
  if (!session) {
    throw new Error('Authentication required for bulk updates')
  }

  const updates = ids.map(id => ({
    id,
    status,
    approved_by: approvedBy,
    approved_at: status === 'approved' ? new Date().toISOString() : null
  }))

  const { data, error } = await supabase
    .from('knowledge')
    .upsert(updates)
    .select()

  if (error) {
    console.error('Bulk update error:', error)
    throw error
  }

  return data
}

// Export configuration for use in other files
export const supabaseConfig = {
  url: SUPABASE_URL,
  anonKey: SUPABASE_ANON_KEY
}

// Edge Functions endpoints
export const edgeFunctions = {
  updateEmbeddings: '/update-knowledge-embeddings',
  searchKnowledge: '/search-knowledge',
  syncCheck: '/sync-check-embeddings'
}